import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { resolveHttpError, withRequestStatus } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { AuthApiClient } from '../../services';
import { authSessionEvents } from '../auth-session';
import { resetPasswordEvents } from './reset-password.events';

/**
 * Signal store for managing the reset password flow.
 *
 * Handles password reset completion after user has received
 * a reset link via email and enters their new password.
 *
 * @example
 * const resetPassword = inject(ResetPasswordStore);
 * const dispatch = injectDispatch(resetPasswordEvents);
 *
 * // In a component
 * dispatch.resetPasswordRequested({
 *   password: 'new-secret-password',
 *   token: 'reset-token-from-email'
 * });
 *
 * // Watch reset password state
 * effect(() => {
 *   if (resetPassword.isSuccess() === 'success') {
 *     // Redirect to login
 *   }
 * });
 */
export const ResetPasswordStore = signalStore(
  withRequestStatus(),
  withReducer(
    on(resetPasswordEvents.resetPasswordRequested, () => ({
      requestStatus: 'loading',
    })),
    on(resetPasswordEvents.resetPasswordSucceeded, () => ({
      requestStatus: 'success',
    })),
    on(resetPasswordEvents.resetPasswordFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    })),
    on(
      authSessionEvents.userCompanyFailed,
      authSessionEvents.userDataFailed,
      () => ({
        requestStatus: 'initial',
      })
    )
  ),
  withEventHandlers(
    (_, events = inject(Events), authService = inject(AuthApiClient)) => ({
      resetPassword$: events
        .on(resetPasswordEvents.resetPasswordRequested)
        .pipe(
          switchMap(({ payload }) =>
            authService.resetPassword(payload.password, payload.token).pipe(
              mapResponse({
                next: ({ token }) => {
                  return resetPasswordEvents.resetPasswordSucceeded({
                    accessToken: token,
                  });
                },
                error: (error: HttpErrorResponse) =>
                  resetPasswordEvents.resetPasswordFailed({
                    error: resolveHttpError(error).code,
                  }),
              })
            )
          )
        ),
    })
  )
);
